import React, { useState, useEffect } from 'react';
import { Button, Typography, List, Spin, message } from 'antd';
import { SearchOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCategoriesWithFoldersAndArticles } from '../../services/freshdeskService';
import styles from './FaqPage.module.css';

const { Title } = Typography;

const FaqPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [categoryArticles, setCategoryArticles] = useState({});
  const [categoryFolders, setCategoryFolders] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState({});

  // URL'den query parametresini al ve search term'i güncelle
  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      setSearchTerm(query);
    }
  }, [searchParams]);

  // Kategorileri, klasörleri ve makaleleri tek seferde çek
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        console.log(`🚀 Fetching categories with folders and articles for language: ${i18n.language}...`);
        const allData = await getCategoriesWithFoldersAndArticles(i18n.language);
        
        console.log('📦 Raw API response:', allData);
        
        // API'den gelen veri formatına göre işle
        const categories = allData.data?.categories || [];
        
        console.log('📋 Processed categories:', categories);
        
        // Her kategori objesinin geçerli olduğundan emin ol
        const validCategories = categories.filter(cat => 
          cat && typeof cat === 'object' && cat.id && cat.name
        );
        
        console.log('✅ Valid categories:', validCategories);
        
        setCategories(validCategories);
        
        // Her kategori için klasörleri ve makaleleri işle
        for (const category of validCategories) {
          console.log(`🔄 Processing category: ${category.name} (ID: ${category.id})`);
          
          // Klasörleri kaydet
          if (category.folders && Array.isArray(category.folders)) {
            setCategoryFolders(prev => ({
              ...prev,
              [category.id]: category.folders
            }));
            
            // Her klasör için makaleleri işle
            const allCategoryArticles = [];
            for (const folder of category.folders) {
              if (folder.articles && Array.isArray(folder.articles)) {
                const processedArticles = folder.articles.map(article => ({
                  id: article.id,
                  title: article.title,
                  description: article.description || article.body || '',
                  updated_at: article.updated_at,
                  status: article.status || 2, // Default olarak published kabul et
                  folder_id: folder.id,
                  folder_name: folder.name
                }));
                allCategoryArticles.push(...processedArticles);
              }
            }
            
            // Kategori için tüm makaleleri kaydet
            setCategoryArticles(prev => ({
              ...prev,
              [category.id]: allCategoryArticles
            }));
          }
        }
        
      } catch (error) {
        console.error('❌ Error fetching data:', error);
        message.error('Failed to load FAQ data. Please try again.');
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [i18n.language]);



  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (categoryArticles[category.id] && categoryArticles[category.id].some(article => 
      article.title.toLowerCase().includes(searchTerm.toLowerCase())
    ))
  );

  const renderCategorySection = (category) => {
    const folders = categoryFolders[category.id] || [];
    const allArticles = categoryArticles[category.id] || [];
    const isArticlesLoading = loadingArticles[category.id];
    
    return (
      <div key={category.id} className={styles.categorySection}>
        <Title level={3} className={styles.categoryTitle}>
          {category.name}
        </Title>
        
        {isArticlesLoading ? (
          <div className={styles.loadingContainer}>
            <Spin size="small" />
            <p>Loading articles...</p>
          </div>
        ) : folders.length > 0 ? (
          <div className={styles.foldersContainer}>
            {folders.map((folder) => {
              const folderArticles = folder.articles || [];
              // API'den gelen makalelerde status alanı yok, tüm makaleleri göster
              const displayedArticles = folderArticles.slice(0, 4); // Her klasörde maksimum 4 makale göster
              const hasMoreArticles = folderArticles.length > 4;
              
              return (
                <div key={folder.id} className={styles.folderSection}>
                  <Title level={4} className={styles.folderTitle}>
                    {folder.name}
                  </Title>
                  
                  {folderArticles.length > 0 ? (
                    <List
                      dataSource={displayedArticles}
                      renderItem={(article) => (
                        <List.Item className={styles.articleItem}>
                          <Link 
                            to={`/faq/article/${article.id}`}
                            className={styles.articleLink}
                          >
                            {article.title}
                          </Link>
                        </List.Item>
                      )}
                      className={styles.articlesList}
                    />
                  ) : (
                    <div className={styles.noArticles}>
                      No articles available in this folder
                    </div>
                  )}
                  
                  {hasMoreArticles && (
                    <div className={styles.folderFooter}>
                      <span className={styles.articleCount}>
                        {folderArticles.length} article{folderArticles.length !== 1 ? 's' : ''} in this folder
                      </span>
                      <Link to={`/faq/category/${category.id}?name=${encodeURIComponent(category.name)}&folder=${folder.id}`}>
                        <Button type="link" className={styles.viewAllButton}>
                          See all articles
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : allArticles.length > 0 ? (
          // Eğer klasör yoksa ama makaleler varsa, eski formatı kullan
          <List
            dataSource={allArticles.slice(0, 6)}
            renderItem={(article) => (
              <List.Item className={styles.articleItem}>
                <Link 
                  to={`/faq/article/${article.id}`}
                  className={styles.articleLink}
                >
                  {article.title}
                </Link>
              </List.Item>
            )}
            className={styles.articlesList}
          />
        ) : (
          <div className={styles.noArticles}>
            No articles available in this category
          </div>
        )}
        
        <div className={styles.categoryFooter}>
          <span className={styles.articleCount}>
            {allArticles.length} article{allArticles.length !== 1 ? 's' : ''} in this category
          </span>
          <Link to={`/faq/category/${category.id}?name=${encodeURIComponent(category.name)}`}>
            <Button type="link" className={styles.viewAllButton}>
              See all articles
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.loadingPage}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorPage}>
        <h2>Failed to Load FAQ</h2>
        <p>Unable to load FAQ categories. Please try again later.</p>
        <Button 
          type="primary" 
          onClick={() => window.location.reload()}
          className={styles.retryButton}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.faqPage}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.container}>
          <Title level={1} className={styles.pageTitle}>
            FAQs
          </Title>
          <p className={styles.subtitle}>
            Search our knowledge base for answers to common questions
          </p>
          
          <div className={styles.searchBar}>
            <div className={styles.searchInput}>
              <SearchOutlined className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Enter a search term and press Enter"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && searchTerm.trim()) {
                    // Search sayfasına yönlendir
                    navigate(`/search?query=${encodeURIComponent(searchTerm.trim())}`);
                  }
                }}
              />
            </div>
          </div>
          
          <div className={styles.commonTopics}>
            <span>Common topics:</span>
            {['account management', 'product', 'booking support', 'payment', 'sales', 'reports'].map((topic, index) => (
              <span 
                key={index}
                className={styles.topic}
                onClick={() => {
                  setSearchTerm(topic);
                  // Navigate to search page with the topic
                  navigate(`/search?query=${encodeURIComponent(topic)}`);
                }}
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.contentContainer}>
          {filteredCategories.length > 0 ? (
            filteredCategories.map(renderCategorySection)
          ) : (
            <div className={styles.noResults}>
              <QuestionCircleOutlined className={styles.noResultsIcon} />
              <Title level={4}>No FAQ categories found</Title>
              <p>Try adjusting your search terms or browse all categories.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FaqPage;
